import Cookies from 'js-cookie';
import { delay } from 'es-toolkit/promise';
import { COOKIE_NAME } from '../../../../contexts/AuthState';
import { isLeftResult } from '../../../../lib/trpcTypeGuards';
import {
  API_BASE_URL,
  ARDRIVE_BACKEND_UPLOAD_MAX_BYTES,
  ARDRIVE_BACKEND_UPLOAD_TIMEOUT_MS,
  ARDRIVE_STAGED_UPLOAD_POLL_INTERVAL_MS,
  ARDRIVE_STAGED_UPLOAD_TIMEOUT_MS,
} from '../../../../lib/config';
import { INSUFFICIENT_CREDITS_ERROR } from '../../../arweaveUpload.constants';
import { uploadArrayBufferWithProgress } from '../../../uploadHelpers';
import type { ProviderTrpcClient, UploadOptions } from '../../types';
import type { ArDriveUploadTarget } from '../shared/types';

type ReportProgress = (
  uploadedBytes: number,
  totalBytes: number,
  onProgress?: UploadOptions['onProgress'],
  additionalInfo?: {
    message?: string;
    isSignatureInProgress?: boolean;
  },
) => void;

type WithRetry = <T>(
  operation: () => Promise<T>,
  operationName: string,
  customMaxRetries?: number,
) => Promise<T>;

type BackendUploadResponsePayload = {
  id?: string;
  url?: string;
  error?: string;
  message?: string;
};

export class ArDriveBackendUploader {
  constructor(
    private readonly options: {
      trpcClient?: ProviderTrpcClient;
      reportProgress: ReportProgress;
      withRetry: WithRetry;
    },
  ) {}

  async uploadSignedDataItem(
    signedBytesBuffer: ArrayBuffer,
    dataItemSize: number,
    uploadOptions?: UploadOptions,
  ): Promise<ArDriveUploadTarget> {
    if (!this.options.trpcClient) {
      return this.options.withRetry(
        () =>
          this.uploadSignedDataItemToBackend(
            signedBytesBuffer,
            dataItemSize,
            uploadOptions,
          ),
        'ArDrive.uploadSignedDataItemToBackend',
        uploadOptions?.maxRetries,
      );
    }

    const staged = await this.stageSignedDataItem(
      signedBytesBuffer,
      dataItemSize,
      uploadOptions,
    );

    return this.options.withRetry(
      () =>
        this.startSignedUploadJob(
          staged.s3Key,
          dataItemSize,
          staged.virtualTotal,
          uploadOptions,
        ),
      'ArDrive.startSignedUploadJob',
      uploadOptions?.maxRetries,
    );
  }

  private async uploadSignedDataItemToBackend(
    signedBytesBuffer: ArrayBuffer,
    dataItemSize: number,
    options?: UploadOptions,
  ): Promise<ArDriveUploadTarget> {
    const jwt = Cookies.get(COOKIE_NAME);
    if (!jwt) {
      throw new Error('Authentication required for ArDrive upload');
    }

    this.assertBackendUploadSize(dataItemSize);

    if (options?.onProgress) {
      this.options.reportProgress(0, dataItemSize, options.onProgress, {
        message: 'Uploading signed data item...',
      });
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      ARDRIVE_BACKEND_UPLOAD_TIMEOUT_MS,
    );

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/ardrive/upload-signed`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/octet-stream',
            'x-data-item-size': String(dataItemSize),
            Authorization: `Bearer ${jwt}`,
          },
          body: signedBytesBuffer,
          signal: controller.signal,
        },
      );

      const { payload, responseText, isJson } =
        await this.parseBackendUploadResponse(response);

      if (!response.ok) {
        const errorMessage =
          payload?.error ??
          payload?.message ??
          responseText ??
          response.statusText;
        const error = new Error(
          `${response.status} ${errorMessage}`.trim(),
        ) as Error & { status?: number };
        error.status = response.status;
        throw error;
      }

      if (!payload?.id || !payload.url) {
        const errorMessage = isJson
          ? 'Unexpected JSON response from backend upload'
          : 'Backend returned non-JSON response. ' +
            'Check VITE_API_BASE_URL or API proxy configuration.';
        throw new Error(errorMessage);
      }

      if (options?.onProgress) {
        this.options.reportProgress(
          dataItemSize,
          dataItemSize,
          options.onProgress,
          {
            message: 'Upload complete',
          },
        );
      }

      return {
        id: payload.id,
        url: payload.url,
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async parseBackendUploadResponse(response: Response): Promise<{
    payload: BackendUploadResponsePayload | null;
    responseText: string;
    isJson: boolean;
  }> {
    const contentType = response.headers.get('content-type') ?? '';
    const responseText = await response.text();
    const isJson = contentType.includes('application/json');

    if (!isJson || !responseText) {
      return {
        payload: null,
        responseText,
        isJson,
      };
    }

    try {
      return {
        payload: JSON.parse(responseText) as BackendUploadResponsePayload,
        responseText,
        isJson,
      };
    } catch {
      return {
        payload: null,
        responseText,
        isJson,
      };
    }
  }

  private async stageSignedDataItem(
    signedBytesBuffer: ArrayBuffer,
    dataItemSize: number,
    options?: UploadOptions,
  ): Promise<{ s3Key: string; virtualTotal: number }> {
    this.assertBackendUploadSize(dataItemSize);

    const trpcClient = this.getTrpcClient();
    const uploadUrlResult =
      await trpcClient.ardrive.generateSignedUploadUrl.mutate({
        dataItemSize,
        releaseId: options?.context?.releaseId,
      });

    if (isLeftResult(uploadUrlResult)) {
      throw new Error(uploadUrlResult.left.message);
    }

    const { uploadUrl, key } = uploadUrlResult.right;
    const virtualTotal = dataItemSize * 2;

    if (options?.onProgress) {
      this.options.reportProgress(0, virtualTotal, options.onProgress, {
        message: 'Uploading signed data item to staging...',
      });
    }

    await uploadArrayBufferWithProgress(
      signedBytesBuffer,
      uploadUrl,
      (percent) => {
        if (!options?.onProgress) {
          return;
        }

        const uploadedBytes = Math.round((percent / 100) * dataItemSize);
        this.options.reportProgress(
          uploadedBytes,
          virtualTotal,
          options.onProgress,
          {
            message: 'Uploading signed data item to staging...',
          },
        );
      },
      'application/octet-stream',
    );

    return { s3Key: key, virtualTotal };
  }

  private async startSignedUploadJob(
    s3Key: string,
    dataItemSize: number,
    virtualTotal: number,
    options?: UploadOptions,
  ): Promise<ArDriveUploadTarget> {
    const trpcClient = this.getTrpcClient();
    const startResult = await trpcClient.ardrive.startSignedUpload.mutate({
      s3Key,
      dataItemSize,
      releaseId: options?.context?.releaseId,
    });

    if (isLeftResult(startResult)) {
      throw new Error(startResult.left.message);
    }

    return this.waitForSignedUploadJob(
      startResult.right.jobId,
      dataItemSize,
      virtualTotal,
      options,
    );
  }

  private async waitForSignedUploadJob(
    jobId: string,
    dataItemSize: number,
    virtualTotal: number,
    options?: UploadOptions,
  ): Promise<ArDriveUploadTarget> {
    const trpcClient = this.getTrpcClient();
    const startTime = Date.now();

    while (true) {
      const statusResult = await trpcClient.ardrive.getSignedUploadStatus.query(
        {
          jobId,
        },
      );

      if (isLeftResult(statusResult)) {
        throw new Error(statusResult.left.message);
      }

      const status = statusResult.right;
      const progressPercent = status.progress ?? 0;

      if (options?.onProgress) {
        const uploadedBytes =
          dataItemSize + Math.round((progressPercent / 100) * dataItemSize);
        const message =
          status.state === 'waiting'
            ? 'Queued for ArDrive upload...'
            : 'Uploading signed data item to ArDrive...';
        this.options.reportProgress(
          uploadedBytes,
          virtualTotal,
          options.onProgress,
          {
            message,
          },
        );
      }

      if (status.state === 'completed') {
        if (!status.result) {
          throw new Error('ArDrive upload completed without a result');
        }

        return status.result;
      }

      if (status.state === 'failed') {
        const reason = status.failedReason ?? 'Signed upload failed';
        const reasonLower = reason.toLowerCase();

        if (
          reasonLower.includes('insufficient') ||
          reasonLower.includes('402')
        ) {
          throw new Error(INSUFFICIENT_CREDITS_ERROR);
        }

        throw new Error(reason);
      }

      if (Date.now() - startTime > ARDRIVE_STAGED_UPLOAD_TIMEOUT_MS) {
        throw new Error('Upload timeout waiting for ArDrive upload');
      }

      await delay(ARDRIVE_STAGED_UPLOAD_POLL_INTERVAL_MS);
    }
  }

  private getTrpcClient(): ProviderTrpcClient {
    if (!this.options.trpcClient) {
      throw new Error('tRPC client is required for staged ArDrive uploads');
    }

    return this.options.trpcClient;
  }

  private assertBackendUploadSize(dataItemSize: number): void {
    if (dataItemSize <= ARDRIVE_BACKEND_UPLOAD_MAX_BYTES) {
      return;
    }

    const sizeError = new Error(
      'Signed data item exceeds backend upload limit',
    ) as Error & { status?: number };
    sizeError.status = 413;
    throw sizeError;
  }
}
